import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { FormControl, FormControlStatus } from '@angular/forms';
import { FilePreviewStatus } from '@modeso/aumico-lib-cpmm-fe/enums/FilePreviewStatus.enum';
import type { ITextSnippetDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { IComplementaryReport, PrintLocation } from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { filter, Subscription, take } from 'rxjs';
import { ComplementaryReportFilesActions } from '../../actions/complementary-report-files.actions';
import { ComplementaryReportFilesProvider } from '../../provider/complementary-report-files.provider';
import { validateNoSquareBrackets } from '../../../../../aumico-lib-core-fe/src/lib';

const NUMBER_OF_ELEMENTS_TO_REMOVE = 1;
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-complementary-reports-page',
    templateUrl: 'complementary-reports.page.html',
    styleUrls: ['complementary-reports.page.scss'],
})
export class ComplementaryReportsPage implements OnInit {
    @ViewChildren('accordionElement') accordionElements: QueryList<ElementRef>;
    @ViewChildren('accordion') accordionComponents: QueryList<any>;
    private accordionSubscription: Subscription;
    private shouldOpenLastAccordion = false;

    public updatedComplementaryReports: IComplementaryReport[] = [];
    private complementaryReportsToBeSaved: IComplementaryReport[] = [];
    complementaryReportsFormsStatuses: FormControlStatus[] = [];

    public reportsAtTheStart: INameId[] = [];
    public reportsAtTheEnd: INameId[] = [];
    public reportsAsAppendix: INameId[] = [];
    public selectedTab = 'reports';

    openAddNewReportDialog: boolean = false;
    // Template picker modal state
    openTemplatePickerDialog: boolean = false;
    private selectedTemplate: ITextSnippetDTO | null = null;

    @Input() set complementaryReports(complementaryReports: IComplementaryReport[]) {
        if (complementaryReports != null) {
            this.updatedComplementaryReports = cloneDeep<IComplementaryReport[]>(complementaryReports);
            this.complementaryReportsToBeSaved = cloneDeep<IComplementaryReport[]>(this.updatedComplementaryReports);
            this.handleReportsPositions();
        }
    }

    @Input() organizationHasCpmmActive: boolean = false;

    @Output() onComplementaryReportsChanged: EventEmitter<IComplementaryReport[]> = new EventEmitter<
        IComplementaryReport[]
    >();

    @Output() complementaryReportsStatus: EventEmitter<FormControlStatus[]> = new EventEmitter<FormControlStatus[]>();

    @Output() printComplementaryReportRequested = new EventEmitter<{ index: number; fileId?: string }>();

    // PDF upload modal state
    public showUploadModal = false;
    public filesToUpload: File[] = [];
    public acceptedExtensions = ['.pdf'];

    // Title input modal state (shown after Scratch or after Upload save)
    public showTitleInputModal = false;
    public control: FormControl = new FormControl<string>('', { nonNullable: true, validators: [validateNoSquareBrackets()] });
    private titleModalMode: 'SCRATCH' | 'PDF' | null = null;
    private pendingUploadFile: File | null = null;

    public complementaryReportPreviews: { [fileId: string]: { previewUrl: string; previewStatus: FilePreviewStatus } } =
        {};

    // Track polling subscriptions and timeouts per file
    private pollSubs = new Map<string, Subscription>();
    private pollTimeouts = new Map<string, any>();
    private pollingDelays = [500, 500, 1000, 1500, 2500, 2500, 2500, 2500, 2500, 2500];

    constructor(
        private cdr: ChangeDetectorRef,
        private actions$: ActionsSubject,
        private readonly complementaryReportFilesProvider: ComplementaryReportFilesProvider,
    ) { }

    // Track which report indices are uploading a file to avoid editor flicker
    private uploadingReportIndices = new Set<number>();

    ngOnInit(): void {
        this.actions$
            .pipe(
                ofType<ReturnType<typeof ComplementaryReportFilesActions.uploadComplementaryReportFileSuccess>>(
                    ComplementaryReportFilesActions.uploadComplementaryReportFileSuccess,
                ),
                untilDestroyed(this),
            )
            .subscribe(({ sortOrderId, printLocation, document }) => {
                this.applyUploadedFileId(
                    sortOrderId,
                    printLocation as unknown as PrintLocation,
                    document?.uuid,
                    document?.name,
                );
                // Clear uploading flag for the just-updated item
                const idx = this.updatedComplementaryReports.findIndex(
                    (r) =>
                        r?.sortOrderId === sortOrderId &&
                        r?.printLocation === (printLocation as unknown as PrintLocation),
                );
                if (idx !== -1) {
                    this.uploadingReportIndices.delete(idx);
                    this.cdr.detectChanges();
                }
            });
        // Store preview status in complementaryReportPreviews
        this.actions$
            .pipe(ofType(ComplementaryReportFilesActions.checkPreviewStatusSuccess), untilDestroyed(this))
            .subscribe(({ fileId, status }) => {
                if (!fileId) return;
                if (!this.complementaryReportPreviews[fileId]) {
                    // initialize entry to avoid runtime errors
                    this.complementaryReportPreviews[fileId] = { previewUrl: '', previewStatus: status };
                } else {
                    this.complementaryReportPreviews[fileId].previewStatus = status;
                }

                this.cdr.detectChanges();

            });

        // Store preview URL in complementaryReportPreviews when it's loaded
        this.actions$
            .pipe(ofType(ComplementaryReportFilesActions.loadPreviewUrlSuccess), untilDestroyed(this))
            .subscribe(({ fileId, url }) => {
                if (!fileId) return;
                if (!this.complementaryReportPreviews[fileId]) {
                    this.complementaryReportPreviews[fileId] = {
                        previewUrl: url,
                        previewStatus: FilePreviewStatus.GENERATED,
                    };
                } else {
                    this.complementaryReportPreviews[fileId].previewUrl = url;
                    this.complementaryReportPreviews[fileId].previewStatus = FilePreviewStatus.GENERATED;
                }
                this.cdr.detectChanges();

            });

        // Listen for openFileSuccess to open the document in a new window
        this.actions$
            .pipe(ofType(ComplementaryReportFilesActions.openFileSuccess), untilDestroyed(this))
            .subscribe(({ fileId, url }) => {
                if (url) {
                    window.open(url, '_blank');
                    // Optionally revoke the URL after a delay to clean up memory
                    setTimeout(() => {
                        this.complementaryReportFilesProvider.dispatchRevokeFileUrl(fileId);
                    }, 1000);
                }
            });
    }

    ngAfterViewInit() {
        this.accordionSubscription = this.accordionComponents.changes.subscribe(() => {
            if (this.shouldOpenLastAccordion) {
                setTimeout(() => {
                    const accordions = this.accordionComponents.toArray();
                    if (accordions.length > 0) {
                        const lastAccordion = accordions[accordions.length - 1];
                        if (lastAccordion && !lastAccordion.expanded) {
                            lastAccordion.onToggle();
                        }
                    }
                    this.shouldOpenLastAccordion = false;
                });
            }
        });
    }

    ngOnDestroy() {
        if (this.accordionSubscription) {
            this.accordionSubscription.unsubscribe();
        }
    }

    public addNewReportClicked(): void {
        this.shouldOpenLastAccordion = true;
        this.createNewReport();
        this.scrollToLastAccordion();
    }

    public onNewReportFromTemplate(): void {
        this.selectedTemplate = null;
        this.openAddNewReportDialog = false;
        this.openTemplatePickerDialog = true;
    }

    public onNewReportFromScratch(): void {
        // Defer creation: ask for title first
        this.openAddNewReportDialog = false;
        this.titleModalMode = 'SCRATCH';
        this.control.reset('');
        this.showTitleInputModal = true;
    }

    public onUploadPDF(): void {
        this.openAddNewReportDialog = false;
        this.showUploadModal = true;
        this.filesToUpload = [];
    }

    private createNewReport(initialTitle?: string | null, initialContent?: string | null): void {
        const newComplementaryReport: IComplementaryReport = {
            title: initialTitle ?? '',
            content: initialContent ?? '',
            printLocation: PrintLocation.END,
            sortOrderId: this.reportsAtTheEnd.length,
            showInTOC: true,
            showPageNumbers: true,
            showTitleInReport: true,
        };
        this.updatedComplementaryReports.push(newComplementaryReport);
        this.complementaryReportsToBeSaved.push(newComplementaryReport);
        this.addNewComplementaryReportToEndPosition();

        this.cdr.detectChanges();

        this.complementaryReportsFormsStatuses.push('INVALID');
        this.complementaryReportsStatus.emit(this.complementaryReportsFormsStatuses);
    }

    public onPageNewFileUploaded(file: File): void {
        this.filesToUpload.push(file);
    }

    public onPageCancelUpload(): void {
        this.showUploadModal = false;
        this.filesToUpload = [];
    }

    public onPageFileUploaded(): void {
        if (this.filesToUpload.length === 0) {
            this.showUploadModal = false;
            return;
        }
        // Single-file policy: take last selected and prompt for title before creating
        const file = this.filesToUpload[this.filesToUpload.length - 1];
        this.pendingUploadFile = file;
        this.showUploadModal = false;
        this.filesToUpload = [];

        // open title modal in PDF mode
        this.titleModalMode = 'PDF';
        this.control.reset('');
        this.showTitleInputModal = true;
    }

    // Title modal handlers
    public onTitleModalCancel(): void {
        // discard pending upload and close modal
        this.pendingUploadFile = null;
        this.titleModalMode = null;
        this.showTitleInputModal = false;
        this.control.reset('');
    }

    public onTitleModalConfirm(): void {
        const title = (this.control.value || '').trim();
        if (!title) {
            return;
        }

        // Create report with the provided title
        this.shouldOpenLastAccordion = true;
        this.createNewReport(title, '');
        this.scrollToLastAccordion();

        const newIndex = this.complementaryReportsToBeSaved.length - 1;

        if (this.titleModalMode === 'PDF' && this.pendingUploadFile) {
            // start upload for created report
            this.uploadingReportIndices.add(newIndex);
            this.cdr.detectChanges();
            const sortOrderId = this.complementaryReportsToBeSaved[newIndex]?.sortOrderId ?? 0;
            const printLocation = this.complementaryReportsToBeSaved[newIndex]?.printLocation as any;
            const formData = new FormData();
            formData.append('file', this.pendingUploadFile);
            this.complementaryReportFilesProvider.dispatchUploadComplementaryReportFile({
                sortOrderId,
                printLocation,
                documentFile: formData,
            });
        }

        // reset modal state
        this.pendingUploadFile = null;
        this.titleModalMode = null;
        this.showTitleInputModal = false;
        this.control.reset('');
    }

    // Expose uploading state to child
    public isReportUploading(index: number | undefined): boolean {
        if (index == null || index < 0) return false;
        return this.uploadingReportIndices.has(index);
    }

    // Template picker handlers
    public onTemplateRowClicked(snippet: ITextSnippetDTO): void {
        this.selectedTemplate = snippet;
    }

    public onTemplatePickerCancel(): void {
        this.openTemplatePickerDialog = false;
        this.selectedTemplate = null;
    }

    public onTemplatePickerSave(): void {
        if (!this.selectedTemplate) {
            // No selection, just close
            this.openTemplatePickerDialog = false;
            return;
        }
        // Create a new report prefilled with the selected template
        this.shouldOpenLastAccordion = true;
        this.createNewReport(this.selectedTemplate.title, this.selectedTemplate.content);
        this.scrollToLastAccordion();
        this.openTemplatePickerDialog = false;
        this.selectedTemplate = null;
    }

    public onDeleteReportClicked(reportIndex: number, complementaryReport: IComplementaryReport): void {
        this.updatedComplementaryReports.splice(reportIndex, NUMBER_OF_ELEMENTS_TO_REMOVE);
        this.complementaryReportsFormsStatuses.splice(reportIndex, NUMBER_OF_ELEMENTS_TO_REMOVE);
        this.complementaryReportsToBeSaved.splice(reportIndex, NUMBER_OF_ELEMENTS_TO_REMOVE);
        this.onComplementaryReportsChanged.emit(this.complementaryReportsToBeSaved);
        this.complementaryReportsStatus.emit(this.complementaryReportsFormsStatuses);

        this.deletePositionAndUpdateReportSortOrder(complementaryReport.printLocation, complementaryReport.sortOrderId);
    }

    public onComplementaryReportChanged(complementaryReportValue: IComplementaryReport, reportIndex: number): void {
        this.complementaryReportsToBeSaved[reportIndex] = complementaryReportValue;
        this.updatedComplementaryReports[reportIndex].title = complementaryReportValue.title;
        this.updateReportPosition(complementaryReportValue, reportIndex);
        this.onComplementaryReportsChanged.emit(this.complementaryReportsToBeSaved);
    }

    public onComplementaryReportStatusChanged(formStatus: FormControlStatus, reportIndex: number): void {
        this.complementaryReportsFormsStatuses[reportIndex] = formStatus;
        this.complementaryReportsStatus.emit(this.complementaryReportsFormsStatuses);
    }

    public onPrintComplementaryReportClicked(request: { index: number; fileId?: string }): void {
        this.printComplementaryReportRequested.emit(request);
    }

    public drop(event: CdkDragDrop<string[]>): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );
        }

        this.updateReportsSortOrderIdsAndPositions();
    }

    private handleReportsPositions(): void {
        this.complementaryReportsToBeSaved.forEach((report, index) => {
            const reportPosition: INameId = {
                id: index.toString(),
                name: report.title,
            };
            switch (report.printLocation) {
                case PrintLocation.START:
                    if (report.sortOrderId != null) {
                        this.reportsAtTheStart[report.sortOrderId] = reportPosition;
                        break;
                    }
                    this.reportsAtTheStart.push(reportPosition);
                    break;
                case PrintLocation.APPENDIX:
                    if (report.sortOrderId != null) {
                        this.reportsAsAppendix[report.sortOrderId] = reportPosition;
                        break;
                    }
                    this.reportsAsAppendix.push(reportPosition);
                    break;
                default:
                    if (report.sortOrderId != null) {
                        this.reportsAtTheEnd[report.sortOrderId] = reportPosition;
                        break;
                    }
                    this.reportsAtTheEnd.push(reportPosition);
            }
        });
        this.updateReportsSortOrderIdsAndPositions();
    }

    private updateReportPosition(complementaryReportValue: IComplementaryReport, reportIndex: number): void {
        const reportPositionId = reportIndex.toString();
        let existingPositionIndex: number | undefined;

        if (complementaryReportValue.printLocation === PrintLocation.APPENDIX) {
            existingPositionIndex = this.reportsAsAppendix.findIndex((report) => report?.id === reportPositionId);
            if (existingPositionIndex !== -1) {
                this.reportsAsAppendix[existingPositionIndex] = {
                    id: reportPositionId,
                    name: complementaryReportValue.title,
                };
            }
        } else if (complementaryReportValue.printLocation === PrintLocation.START) {
            existingPositionIndex = this.reportsAtTheStart.findIndex((report) => report?.id === reportPositionId);
            if (existingPositionIndex !== -1) {
                this.reportsAtTheStart[existingPositionIndex] = {
                    id: reportPositionId,
                    name: complementaryReportValue.title,
                };
            }
        } else {
            existingPositionIndex = this.reportsAtTheEnd.findIndex((report) => report?.id === reportPositionId);
            if (existingPositionIndex !== -1) {
                this.reportsAtTheEnd[existingPositionIndex] = {
                    id: reportPositionId,
                    name: complementaryReportValue.title,
                };
            }
        }
    }

    private resetReportsPositions(): void {
        this.reportsAsAppendix = [];
        this.reportsAtTheEnd = [];
        this.reportsAtTheStart = [];
    }

    private updateReportsSortOrderIdsAndPositions(): void {
        this.reportsAtTheStart.forEach((report, index) => {
            this.complementaryReportsToBeSaved[report.id].sortOrderId = index;
            this.updatedComplementaryReports[report.id].sortOrderId = index;

            this.complementaryReportsToBeSaved[report.id].printLocation = PrintLocation.START;
            this.updatedComplementaryReports[report.id].printLocation = PrintLocation.START;
        });

        this.reportsAtTheEnd.forEach((report, index) => {
            this.complementaryReportsToBeSaved[report.id].sortOrderId = index;
            this.updatedComplementaryReports[report.id].sortOrderId = index;

            this.complementaryReportsToBeSaved[report.id].printLocation = PrintLocation.END;
            this.updatedComplementaryReports[report.id].printLocation = PrintLocation.END;
        });

        this.reportsAsAppendix.forEach((report, index) => {
            this.complementaryReportsToBeSaved[report.id].sortOrderId = index;
            this.updatedComplementaryReports[report.id].sortOrderId = index;

            this.complementaryReportsToBeSaved[report.id].printLocation = PrintLocation.APPENDIX;
            this.updatedComplementaryReports[report.id].printLocation = PrintLocation.APPENDIX;
        });

        this.onComplementaryReportsChanged.emit(this.complementaryReportsToBeSaved);
    }

    private addNewComplementaryReportToEndPosition(): void {
        const newReportIndex = (this.complementaryReportsToBeSaved.length - 1).toString();
        this.reportsAtTheEnd.push({
            id: newReportIndex,
            name: this.complementaryReportsToBeSaved[newReportIndex].title,
        });
    }

    private deletePositionAndUpdateReportSortOrder(
        printLocation: PrintLocation | undefined,
        sortOrderId: number,
    ): void {
        this.resetReportsPositions();
        this.handleReportsPositions();
        switch (printLocation) {
            case PrintLocation.START:
                this.reportsAtTheStart.splice(sortOrderId, NUMBER_OF_ELEMENTS_TO_REMOVE);

                this.reportsAtTheStart.forEach((report, index) => {
                    this.complementaryReportsToBeSaved[report.id].sortOrderId = index;
                    this.updatedComplementaryReports[report.id].sortOrderId = index;
                });

                break;
            case PrintLocation.APPENDIX:
                this.reportsAsAppendix.splice(sortOrderId, NUMBER_OF_ELEMENTS_TO_REMOVE);
                this.reportsAsAppendix.forEach((report, index) => {
                    this.complementaryReportsToBeSaved[report.id].sortOrderId = index;
                    this.updatedComplementaryReports[report.id].sortOrderId = index;
                });
                break;
            default:
                this.reportsAtTheEnd.splice(sortOrderId, NUMBER_OF_ELEMENTS_TO_REMOVE);

                this.reportsAtTheEnd.forEach((report, index) => {
                    this.complementaryReportsToBeSaved[report.id].sortOrderId = index;
                    this.updatedComplementaryReports[report.id].sortOrderId = index;
                });
        }
    }

    onTitleChange(title: string, complementaryReportIndex: number): void {
        this.updatedComplementaryReports[complementaryReportIndex].title = title;
        this.complementaryReportsToBeSaved[complementaryReportIndex].title = title;
        const reportId = complementaryReportIndex.toString();
        const printLocation = this.complementaryReportsToBeSaved[complementaryReportIndex].printLocation;

        this.onComplementaryReportsChanged.emit(this.complementaryReportsToBeSaved);
        this.updateReportTitleInSortOrder(printLocation, reportId, title);
    }

    private updateReportTitleInSortOrder(printLocation: PrintLocation, reportId: string, title: string): void {
        let targetList: INameId[] = [];

        switch (printLocation) {
            case PrintLocation.START:
                targetList = this.reportsAtTheStart;
                break;
            case PrintLocation.END:
                targetList = this.reportsAtTheEnd;
                break;
            case PrintLocation.APPENDIX:
                targetList = this.reportsAsAppendix;
                break;
        }

        const reportIndex = targetList.findIndex((report) => report.id === reportId);
        if (reportIndex !== -1) {
            targetList[reportIndex].name = title;
        }
    }

    public onTabClick(tab: string): void {
        this.selectedTab = tab;
    }

    private scrollToLastAccordion(): void {
        setTimeout(() => {
            const elements = this.accordionElements.toArray();
            const last = elements[elements.length - 1];

            if (last?.nativeElement) {
                last.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    }

    public onFormStatusChange(formStatus: FormControlStatus, reportIndex: number): void {
        this.complementaryReportsFormsStatuses[reportIndex] = formStatus;
        this.complementaryReportsStatus.emit(this.complementaryReportsFormsStatuses);
    }

    private applyUploadedFileId(
        sortOrderId: number,
        printLocation: PrintLocation,
        fileUuid?: string,
        fileName?: string,
    ): void {
        if (fileUuid == null) {
            return;
        }

        const idx = this.updatedComplementaryReports.findIndex(
            (r) => r?.sortOrderId === sortOrderId && r?.printLocation === printLocation,
        );
        if (idx === -1) {
            return;
        }

        // Replace existing (single file policy)
        // Only assign when values are provided to avoid adding `undefined` properties
        if (fileUuid != null) {
            this.updatedComplementaryReports[idx].fileId = fileUuid;
            this.complementaryReportsToBeSaved[idx].fileId = fileUuid;
        }
        if (fileName != null) {
            this.updatedComplementaryReports[idx].fileName = fileName;
            this.complementaryReportsToBeSaved[idx].fileName = fileName;
        }

        // Emit updated list so container/page above can persist on Save
        this.onComplementaryReportsChanged.emit(this.complementaryReportsToBeSaved);

        // Refresh view if needed
        this.cdr.detectChanges();
    }

    public onPreviewRequested(fileId: string): void {
        // Handle the preview request here
        this.onStartPolling(fileId);
    }
    onPreviewClosed(fileId: string): void {
        // Stop polling when the preview modal is closed
        this.onStopPolling(fileId);
    }

    onStartPolling(fileId: string): void {
        const delays = [...this.pollingDelays];
        const tick = () => {
            if (!delays.length) {
                this.onStopPolling(fileId);
                return;
            }
            const delay = delays.shift() as number;
            const timeout = setTimeout(() => {
                const sub = this.actions$
                    .pipe(
                        ofType<ReturnType<typeof ComplementaryReportFilesActions.checkPreviewStatusSuccess>>(
                            ComplementaryReportFilesActions.checkPreviewStatusSuccess,
                        ),
                        filter((a) => a.fileId === fileId),
                        take(1),
                        untilDestroyed(this),
                    )
                    .subscribe(({ status }) => {
                        if (status === FilePreviewStatus.GENERATED) {
                            this.complementaryReportFilesProvider.dispatchLoadPreviewUrl(fileId);
                            this.onStopPolling(fileId);
                        } else if (status === FilePreviewStatus.PENDING || status === FilePreviewStatus.SCANNING) {
                            tick();
                        } else if (status === FilePreviewStatus.NOT_STARTED) {
                            this.complementaryReportFilesProvider.dispatchRequestPreviewGeneration(fileId);
                            tick();
                        } else {
                            this.onStopPolling(fileId);
                        }
                    });
                this.pollSubs.set(fileId, sub);
                this.complementaryReportFilesProvider.dispatchCheckPreviewStatus(fileId);
            }, delay);
            this.pollTimeouts.set(fileId, timeout);
        };
        tick();
    }

    onStopPolling(fileId: string): void {
        const sub = this.pollSubs.get(fileId);
        if (sub) {
            sub.unsubscribe();
            this.pollSubs.delete(fileId);
        }
        const timeout = this.pollTimeouts.get(fileId);
        if (timeout) {
            clearTimeout(timeout);
            this.pollTimeouts.delete(fileId);
        }
    }

    onOpenFile(fileId: string): void {
        this.complementaryReportFilesProvider.dispatchOpenFile(fileId);
    }

    onDownloadFile({ documentName, documentId }): void {
        this.complementaryReportFilesProvider.dispatchDownloadFile(documentId, documentName);
    }
}
